import { faBars, faDownload, faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Flex, Row, Tag } from "antd";
import Table, { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import CustomizeTableModal from "../../components/customize-table";
import FilterDrawer from "../../components/filter-drawer";
import TableViewDropdown from "../../components/table-view-dropdown";
import { DATE_FORMAT, DATE_FORMAT_DDMMYYYY_HHMMSS } from "../../constants/common";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { PERMISSIONS } from "../../constants/permission";
import { useDropdownsReportData } from "../../hooks/useDropdownsReportData";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useUpdateTableView } from "../../hooks/useTableView";
import { useTableViews } from "../../hooks/useTableViews";
import { IColumnConfig, ISortInfo } from "../../interfaces/common";
import { ITableViewColumn, ITableViewReport, TableViewFilters } from "../../interfaces/table-view";
import { ReportServices } from "../../services/report";
import {
  countNonEmptyFields,
  formatDateFieldsFlat,
  formatNumberFixed,
  getAllDisplayedColumnFilters,
  renderClassNameStatusApWorkflow,
} from "../../utils/helper";
import FilterReport from "./components/filter";
import SelectFieldsReport from "./components/select-fields-report";
import "./index.scss";
import Loading from "../../components/loading";

const TABLE_NAME = "financial_report";

const renderFieldTable = (fieldName: string, value: any) => {
  const dateFields = [
    "irReceivedAt",
    "irInvoiceDate",
    "sirInvoiceDate",
    "sirfTransactionDate",
    "espBookingDate",
    "espDepartureDate",
    "espReturnDate",
    "espComponentStartDate",
    "espComponentEndDate",
    "espDueDate",
    "espExportDate",
    "bscExportDate",
    "apwFlagAddAt",
    "apwTeamAddAt",
    "apwCommentAt",
    "twFlagAddAt",
    "twTeamAddAt",
    "twCommentAt",
    "otwFlagAddAt",
    "otwTeamAddAt",
    "otwCommentAt",
    "fwFlagAddAt",
    "fwTeamAddAt",
    "fwCommentAt",
  ];

  const numberFields = ["espNumberOfPax", "espNumberOfNight", "espNumberOfPaxSupplier", "espNumberOfRoom"];
  const costFields = ["irTotalAmount", "sirAmount", "sirfAmount", "espAmount", "espGbpCost"];
  const flagFields = ["twFlag", "otwFlag", "fwFlag"];
  const datetimeFields = ["espExportedDatetime"];

  if (dateFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT) : value;
  }

  if (datetimeFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT_DDMMYYYY_HHMMSS) : value;
  }

  if (numberFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value}</div>;
  }

  if (costFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value ? formatNumberFixed(value) : value}</div>;
  }

  if (flagFields.includes(fieldName)) {
    return value && value.map((item: string) => <Tag key={item}>{item}</Tag>);
  }

  if (fieldName === "status") {
    return <span className={renderClassNameStatusApWorkflow(value)}>{value}</span>;
  }

  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "";
  }

  return value;
};

const ReportPage: React.FC = () => {
  const [dataList, setDataList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isGenReport, setIsGenReport] = useState<boolean>(false);
  const [isExport, setIsExport] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });

  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [isFetchedMaterData, setIsFetchedMaterData] = useState(false);

  const hasEditorReport = useHasPermission([PERMISSIONS.FINANCIAL_REPORT_EDITOR]);

  const {
    dataTableView,
    setDataTableView,
    loading: loadingTableView,
    refetchTableViews,
  } = useTableViews({
    tableName: TABLE_NAME,
  });

  const currentTableView = dataTableView?.currentView!;

  const { filters = {}, columns: columnConfigs = [], reports = [], id: idTable = "" } = currentTableView || {};

  const isDefaultView = !!dataTableView?.defaultView;

  const { updateTableView } = useUpdateTableView();

  const activeFilterCount = countNonEmptyFields(filters);

  const displayedColumnFilters = getAllDisplayedColumnFilters(columnConfigs);
  const { data: dropdownsData } = useDropdownsReportData(displayedColumnFilters, isFetchedMaterData, true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        ...formatDateFieldsFlat(filters),
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };
      const response: any = await ReportServices.getFinancialReportViewId(idTable!, params);
      setDataList(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch Api error:", error);
    } finally {
      setLoading(false);
      setIsFetchedMaterData(true);
    }
  };

  useEffect(() => {
    if (!loadingTableView && !!idTable) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTableView, pagination.current, pagination.pageSize, sortInfo, idTable, filters]);

  const generateColumns = (config: ITableViewColumn[]): ColumnType<any>[] =>
    config
      .filter((c) => c.isDisplay)
      .map((col) =>
        col.children && col.children.length > 0
          ? {
              title: col.label,
              width: 150,
              children: generateColumns(col.children),
            }
          : {
              title: col.label,
              dataIndex: col.column,
              key: col.column,
              sorter: true,
              align: "left",
              sortDirections: ["ascend", "descend"],
              width: 150,
              render: (value: any) => renderFieldTable(col.column, value),
            }
      );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => generateColumns(columnConfigs), [columnConfigs]);

  const handleGenerateReport = async (data: ITableViewReport[]) => {
    try {
      setIsGenReport(true);
      await ReportServices.generateReport({ reports: data });
      refetchTableViews();
    } catch (err: any) {
      console.log(err);
    } finally {
      setIsGenReport(false);
    }
  };

  const handleSaveCustomizeTable = async (configs: IColumnConfig[]) => {
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, columns: configs } });
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const handleApplyFilters = (values: TableViewFilters) => {
    const newFilters = { ...filters, ...values };
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, filters: newFilters } });
    if (isDefaultView) {
      updateTableView({
        tableName: TABLE_NAME,
        filters: newFilters,
      });
    }
    setOpenFilter(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleExport = async () => {
    try {
      setIsExport(true);
      const dateTime = dayjs(new Date()).format(DATE_FORMAT_DDMMYYYY_HHMMSS);
      const fileName = `financial-report-${dateTime}`;
      const response: Blob = await ReportServices.exportExcel(idTable, formatDateFieldsFlat(filters));

      const blobUrl = window.URL.createObjectURL(response);

      // Trigger download
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (err: any) {
      console.log(err);
    } finally {
      setIsExport(false);
    }
  };

  return (
    <>
      {loadingTableView && <Loading />}
      <div className="report-page">
        <h2 className="title-page">Financial Report</h2>
        <Row gutter={24}>
          {hasEditorReport && (
            <Col span={6}>
              <SelectFieldsReport data={reports} onSubmit={handleGenerateReport} isLoading={isGenReport} />
            </Col>
          )}

          <Col span={hasEditorReport ? 18 : 24}>
            <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
              <div className="sub-title">Report Table</div>
              <Flex gap={12}>
                <Button type="primary" onClick={handleExport} loading={isExport}>
                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: "16px" }} />
                  Export to Excel
                </Button>
                <Button color="primary" variant="outlined" onClick={() => setShowModal(true)}>
                  <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
                  Customise
                </Button>
                <Button color="primary" variant="outlined" onClick={() => setOpenFilter(true)}>
                  <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </Button>
                <TableViewDropdown
                  tableName={TABLE_NAME}
                  dataTableView={dataTableView!}
                  onTrigger={() => {
                    refetchTableViews();
                  }}
                />
              </Flex>
            </Flex>
            <Table
              dataSource={dataList}
              loading={loading || loadingTableView}
              rowKey={(record) =>
                `${record?.irId}_${record?.sirId}_${record?.sirfId}_${record?.irId}_${record?.bscId}_${record?.apwId}_${record?.fwId}_${record?.twId}_${record?.espId}`
              }
              tableLayout="fixed"
              columns={columns}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `Total ${total} items`,
              }}
              onChange={handleTableChange}
              size="small"
              className="table-fm"
              rowClassName={(record, index) => {
                let className = index % 2 === 0 ? "highlight-row" : "";
                return className.trim();
              }}
              scroll={{ x: 1200, y: "calc(100vh - 390px)" }}
              bordered
            />
          </Col>
        </Row>

        {showModal && (
          <CustomizeTableModal
            open={showModal}
            onClose={() => setShowModal(false)}
            configsValue={columnConfigs}
            onSave={handleSaveCustomizeTable}
            tableName={TABLE_NAME}
            isSave={isDefaultView}
          />
        )}
        {openFilter && (
          <FilterDrawer
            title="Filter Financial Report"
            open={openFilter}
            onClose={() => setOpenFilter(false)}
            onApply={handleApplyFilters}
            values={filters}
            children={<FilterReport dropdownsData={dropdownsData} displayedColumnFilters={displayedColumnFilters} />}
          />
        )}
      </div>
    </>
  );
};

export default ReportPage;
